import { Content } from "../../../general/modules/commons/content/content";
import { ContentHeader } from "../../../general/modules/commons/content/contentHeader";
import {
  Layout,
  LayoutContent,
  LayoutHeader,
  LayoutHeaderActions,
  LayoutHeaderTitle,
} from "../../../general/modules/commons/layout/layout";
import {
  Breadcrumb,
  BreadCrumbItem,
} from "../../../general/modules/commons/breadcrumb/breadcrumb";
import { useAppDispatch } from "../../../../hooks/useTypedSelector";
import { logout } from "../../../auth/infrastructure/slices/authSlice/authSlice";
import useCrudTable from "../../../../hooks/useCrudTable";
import useCrudFilters from "../../../../hooks/useCrudFilters";
import { useTranslation } from "react-i18next";
import { requestSchema } from "../../domain/schemas/RequestSchema";
import { RequestTableFilters } from "../features/RequestTableFilters";
import { useEffect, useState, useMemo } from "react";
import { AllRequestQueryResponseModel } from "../../domain/models/requestModel";
import { getAllRequestStates } from "../../infrastructure/slices/requestMutateSlice";
import { useCrudExport } from "../../../../hooks/useCrudExport";
import { useRequestsPageColumns } from "../../infrastructure/hooks/useRequestsPageColumns";

export const RequestsPage = () => {
  const { t } = useTranslation("common");

  const dispatch = useAppDispatch();

  const title = t("Requests");

  const crudFilters = useCrudFilters(
    {
      searchBy: "SearchByRootRequest",
      fields: {
        state: {
          in: [
            "concept",
            "submitted",
            "deliver",
            "taken_from_storage",
            "checked_for_delivery",
            "loaded_for_delivery",
            "pickup",
            "loaded_for_storage",
            "checked_for_storage",
          ],
        },
      },
    },
    "requestFilter",
  );

  const translatedColumns = useRequestsPageColumns();

  // Extract license plate filter order for custom sorting
  const licencePlateOrder = useMemo(() => {
    const tiresetFilter = crudFilters.filters?.fields?.tireset;
    if (tiresetFilter && "fields" in tiresetFilter) {
      const carFilter = tiresetFilter.fields?.car;
      if (carFilter && "fields" in carFilter) {
        const licenceFilter = carFilter.fields?.licence;
        if (
          licenceFilter &&
          "in" in licenceFilter &&
          Array.isArray(licenceFilter.in)
        ) {
          return licenceFilter.in as string[];
        }
      }
    }
    return null;
  }, [crudFilters.filters]);

  // Custom sorting function for license plates
  const customSortData = useMemo(() => {
    if (!licencePlateOrder) {
      return null;
    }

    return (data: unknown[]) => {
      return [...data].sort((a, b) => {
        const requestA = a as { source?: { car?: { licence?: string } } };
        const requestB = b as { source?: { car?: { licence?: string } } };
        const licenceA = requestA?.source?.car?.licence;
        const licenceB = requestB?.source?.car?.licence;

        if (!licenceA || !licenceB) return 0;

        const indexA = licencePlateOrder.indexOf(licenceA);
        const indexB = licencePlateOrder.indexOf(licenceB);

        // If both license plates are in the filter order, sort by that order
        if (indexA !== -1 && indexB !== -1) {
          return indexA - indexB;
        }

        // If only one is in the order, prioritize it
        if (indexA !== -1) return -1;
        if (indexB !== -1) return 1;

        // If neither is in the order, maintain current order
        return 0;
      });
    };
  }, [licencePlateOrder]);

  const [allRequestStates, setAllRequestStates] = useState<
    AllRequestQueryResponseModel | undefined
  >();

  useEffect(() => {
    if (!allRequestStates) {
      void dispatch(getAllRequestStates())
        .unwrap()
        .then((response) => {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          setAllRequestStates(response);
        });
    }
  }, []);

  const buildStateOptions = (key: keyof AllRequestQueryResponseModel) => {
    if (allRequestStates) {
      return allRequestStates[key].map((state) => {
        return {
          value: state,
          label: state,
        };
      });
    }
  };

  const crud = useCrudTable({
    prefix: "requests",
    entity: "Request",
    columns: translatedColumns,
    schema: requestSchema,
    filters: crudFilters.filters,
    columnAction: "/requests/:id",
    bulkRoles: ["dbh-manager", "super-admin"],
    bulkFields: [
      { field: "pickup_and_delivery_date", options: [], date: true },
      { field: "in_out_storage", options: [], date: true },
      {
        field: "state_pickup",
        options: buildStateOptions("pickupStates") as {
          label: string;
          value: string;
        }[],
        date: false,
      },
      {
        field: "state_deliver",
        options: buildStateOptions("deliverStates") as {
          label: string;
          value: string;
        }[],
        date: false,
      },
      {
        field: "invoice_status",
        options: [
          { label: "Invoiced", value: "invoiced" },
          { label: "To invoice", value: "to_invoice" },
        ],
        date: false,
      },
    ],
    queryExclude: ["source_id"],
    enableCustomSorting: licencePlateOrder != null,
    customSortData: customSortData,
    defaultSorting: licencePlateOrder
      ? []
      : [
          {
            id: "in_out_storage",
            desc: true,
          },
        ],
  });

  const requestExport = useCrudExport({
    pageCount: crud.tableInstance.getPageCount(),
    queryFunction: "exportRequests",
    queryData: [
      {
        source: [
          {
            "... on Tireset": [
              { car: ["licence"] },
              { position: ["label"] },
              "sleep_set",
            ],
          },
        ],
      },
      { relation: ["name"] },
      "type",
      "state",
      "pickup_and_delivery_date",
      "in_out_storage",
    ],
    filters: crudFilters.filters,
  });

  return (
    <Layout
      header={
        <LayoutHeader logout={() => void dispatch(logout())}>
          <div className="d-flex flex-wrap align-items-center justify-content-between w-100">
            <div>
              <LayoutHeaderTitle title={title} />
            </div>
            <div className="flex-grow-1 mx-md-3 my-2 my-md-0">
              <Breadcrumb>
                <BreadCrumbItem>{title}</BreadCrumbItem>
              </Breadcrumb>
            </div>
            <div className="mt-2 mb-2 mt-md-0">
              <LayoutHeaderActions>
                {crud.addButton}
                {requestExport.exportButton}
              </LayoutHeaderActions>
            </div>
          </div>
        </LayoutHeader>
      }
    >
      <LayoutContent>
        <Content>
          <ContentHeader title={title} className="mb-3" />
          <RequestTableFilters {...crudFilters} />
          {crud.bulk}
          {crud.table}
        </Content>
      </LayoutContent>
    </Layout>
  );
};
